import { useEffect, useState } from "react";
import { Star, MapPin, BookOpen, ArrowRight, X } from "lucide-react";
import MediaImg from "@/components/MediaImg";

const DEFAULT_PLACEMENT = [
  { name: "Google" }, { name: "KPMG" }, { name: "Deloitte" }, { name: "Accenture" },
  { name: "TCS" }, { name: "Infosys" }, { name: "Wipro" }, { name: "EY" },
  { name: "HCLTech" }, { name: "IBM" },
];

const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

function Stars({ n = 5 }) {
  return (
    <div className="flex items-center gap-0.5 mt-0.5" aria-label={`${n} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`w-3.5 h-3.5 ${i < n ? "fill-amber-400 text-amber-400" : "fill-slate-200 text-slate-200"}`} />
      ))}
    </div>
  );
}

function CompanyLogo({ logo, name, alt, description, className }) {
  const [err, setErr] = useState(false);
  if (!logo || err) return <span className="text-xs font-extrabold text-slate-700" title={description || undefined}>{name}</span>;
  return (
    <span title={description || undefined} className="inline-flex">
      <MediaImg src={logo} alt={alt || name || "Company logo"} onError={() => setErr(true)} className={className} sizes="120px" />
    </span>
  );
}

function Avatar({ t, size = "w-10 h-10" }) {
  const [err, setErr] = useState(false);
  if (!t.photo || err) {
    return <span className={`${size} rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-extrabold shrink-0`}>{initials(t.name)}</span>;
  }
  return <img src={t.photo} alt={t.name} onError={() => setErr(true)} className={`${size} rounded-full object-cover shrink-0`} loading="lazy" />;
}

function StoryDetail({ t, onClose }) {
  return (
    <div className="flex flex-col sm:flex-row gap-5">
      <div className="sm:w-40 shrink-0">
        <Avatar t={t} size="w-20 h-20 sm:w-40 sm:h-44 sm:rounded-2xl" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xl font-extrabold text-[#17263B] tracking-tight">{t.name}</p>
            {(t.role || t.company) && <p className="text-sm font-semibold text-slate-600 mt-0.5">{[t.role, t.company && `at ${t.company}`].filter(Boolean).join(" ")}</p>}
            {t.location && <p className="flex items-center gap-1 text-xs text-slate-400 mt-1"><MapPin className="w-3.5 h-3.5" /> {t.location}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" data-testid="story-popover-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed mt-3">"{t.long_story || t.quote}"</p>
        <div className="flex flex-wrap items-center gap-3 mt-5">
          {t.program_completed && (
            <span className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3.5 py-2.5">
              <BookOpen className="w-4 h-4 text-amber-500" />
              <span><span className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold">Program Completed</span>
              <span className="block text-xs font-extrabold text-slate-800">{t.program_completed}</span></span>
            </span>
          )}
          {t.story_link && (
            <a href={t.story_link} target={t.story_link.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B] px-5 py-3 text-sm font-extrabold transition-colors" data-testid="story-cta">
              View Success Story <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SuccessStories({ cms }) {
  const items = (cms?.testimonials || []).filter((t) => t.visible !== false && t.name);
  const logos = (cms?.placement_logos || []).filter((l) => l.visible !== false && l.name);
  const placement = logos.length ? logos : DEFAULT_PLACEMENT;

  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (open == null) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!items.length && !placement.length) return null;
  const openStory = open != null ? items[open] : null;

  return (
    <section className="relative bg-slate-50 border-y border-slate-200 overflow-hidden" data-testid="success-stories">
      <style>{`
        @keyframes story-pop { from { opacity: 0; transform: translate(-50%, -48%) scale(0.96); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
        @keyframes story-sheet { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes story-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .story-marquee { animation-name: story-marquee; animation-timing-function: linear; animation-iteration-count: infinite; }
        .story-marquee:hover, .story-marquee-paused { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .story-anim { animation: none !important; } .story-marquee { animation: none; } }
      `}</style>
      <div className="absolute -right-24 -top-16 w-72 h-72 rounded-full bg-amber-100/60 blur-3xl" aria-hidden="true" />
      <div className="absolute -left-24 bottom-10 w-72 h-72 rounded-full bg-amber-100/50 blur-3xl" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative">
        {items.length > 0 && (
          <>
            <div className="flex items-end justify-between gap-4">
              <div className="max-w-xl">
                <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.2em] text-amber-500" data-testid="stories-eyebrow">
                  Success Stories <span className="h-px w-10 bg-amber-400" />
                </p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#17263B] mt-3" data-testid="stories-title">
                  Careers that moved <span className="text-[#F5B301]">forward</span>
                </h2>
                <p className="text-sm sm:text-base text-slate-500 mt-3">From learning to leading. Our learners are building successful careers across top companies.</p>
              </div>
            </div>

            <div className="mt-10 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
              <div className={`story-marquee flex items-center w-max ${open != null ? "story-marquee-paused" : ""}`} style={{ animationDuration: `${Math.max(items.length, 2) * 6}s` }} data-testid="story-track">
                {[...items, ...items].map((t, i) => {
                  const dup = i >= items.length;
                  const idx = i % items.length;
                  return (
                  <div key={i} className="shrink-0 px-2" aria-hidden={dup || undefined}>
                    <div onClick={() => setOpen(idx)}
                      className={`flex items-center gap-3.5 rounded-full border bg-white pl-2 pr-3 py-2 cursor-pointer transition-all duration-300 ${open === idx ? "border-amber-400 shadow-lg" : "border-slate-200 hover:border-amber-400 hover:shadow-lg"}`}
                      data-testid={dup ? undefined : `story-card-${idx}`}>
                      <Avatar t={t} size="w-11 h-11" />
                      <div className="min-w-0">
                        <p className="text-sm font-extrabold text-[#1D4ED8] whitespace-nowrap">{t.name}</p>
                        <Stars n={t.rating || 5} />
                      </div>
                      {(t.company || t.company_logo) && (
                        <CompanyLogo logo={t.company_logo} name={t.company} alt={t.company_logo_alt} description={t.company_logo_description} className="h-6 max-w-[90px] object-contain ml-2" />
                      )}
                      <button onClick={(e) => { e.stopPropagation(); setOpen(idx); }} onMouseEnter={() => { if (window.innerWidth >= 1024) setOpen(idx); }} tabIndex={dup ? -1 : 0}
                        className="ml-1 w-8 h-8 rounded-full bg-amber-100 hover:bg-[#FFC72C] text-slate-700 flex items-center justify-center font-extrabold tracking-widest transition-colors shrink-0"
                        aria-label={`View ${t.name}'s story`} data-testid={dup ? undefined : `story-expand-${idx}`}>•••</button>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {placement.length > 0 && (
          <div className="mt-16 grid lg:grid-cols-[320px_1fr] gap-8 items-center" data-testid="placement-strip">
            <div>
              <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.2em] text-amber-500">Placement Network <span className="h-px w-10 bg-amber-400" /></p>
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#17263B] mt-3">Our learners get <span className="text-[#F5B301]">hired by</span></h2>
              <p className="text-sm sm:text-base text-slate-500 mt-3">Our learners are working with leading global companies across industries.</p>
            </div>
            <div className="overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
              <div className="story-marquee flex items-center w-max py-1" style={{ animationDuration: `${Math.max(placement.length, 2) * 3.5}s` }} data-testid="placement-track">
                {[...placement, ...placement].map((l, i) => (
                  <div key={i} aria-hidden={i >= placement.length || undefined}
                    className="mx-1.5 shrink-0 rounded-xl border border-slate-200 bg-white px-6 h-20 w-44 flex items-center justify-center hover:border-amber-300 hover:shadow-md transition-all"
                    data-testid={i < placement.length ? `placement-logo-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined}>
                    <CompanyLogo logo={l.logo} name={l.name} alt={l.alt} description={l.description} className="h-7 max-w-[110px] object-contain" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {openStory && (
        <>
          <div className="fixed inset-0 z-40 bg-slate-900/20" onClick={() => setOpen(null)} data-testid="story-popover-backdrop" />
          <div className="story-anim hidden md:block fixed z-50 left-1/2 top-1/2 w-[540px] max-w-[92vw] rounded-2xl border border-slate-200 bg-white p-7 shadow-2xl"
            style={{ animation: "story-pop 0.25s ease-out both" }} role="dialog" aria-modal="true" aria-label={`${openStory.name} success story`} data-testid="story-popover">
            <StoryDetail t={openStory} onClose={() => setOpen(null)} />
          </div>
          <div className="story-anim md:hidden fixed z-50 inset-x-3 bottom-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl max-h-[80vh] overflow-y-auto"
            style={{ animation: "story-sheet 0.3s ease-out both" }} role="dialog" aria-modal="true" aria-label={`${openStory.name} success story`} data-testid="story-sheet">
            <StoryDetail t={openStory} onClose={() => setOpen(null)} />
          </div>
        </>
      )}
    </section>
  );
}
